/**
 * QuestionsSection — шарнир отчёта.
 *
 * Всё выше отвечает на «что происходит в рекламе». Здесь это переходит в
 * «что с этим делать», причём в единственной валюте, которая у директора
 * есть, — в вопросе на планёрке.
 *
 * Задать вопрос мало: ответ ещё нужно оценить, а для этого надо знать
 * Директ. Поэтому под каждым вопросом лежит эталон — как звучит рабочий
 * ответ и почему он рабочий. Читателю остаётся сверить услышанное с ним.
 *
 * Отговорку в пару к эталону не показываем: она учит распознавать плохое,
 * но не тому, каким ответ должен быть, — а нужно именно второе.
 *
 * Один столбец, а не два: с раскрытыми эталонами колонки дрались бы за
 * ширину, и разбор ужался бы до нечитаемого.
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Icon} from 'ui/components/atoms/Icon';
import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {spacingPx} from 'ui/components/layout/spacing';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';
import {MotionDurationBase, MotionEasingStandard} from 'ui/tokens/ts/motion';

import {MEASURE, MEASURE_WIDE} from '../helpers';
import type {AnswerExample, Question} from '../types';
import {stripTypography} from '../typograph';
import {CopyButton} from './CopyButton';
import {Disclosure} from './Disclosure';
import {SplitCard, SplitPanel} from './SplitCard';

export interface QuestionsSectionProps {
  questions: Question[];
}

/**
 * Кнопка копирования у каждого вопроса — инструмент, а не содержание. Пять
 * одинаковых кнопок в столбик читаются как узор и отвлекают от самих
 * вопросов, поэтому по умолчанию их не видно.
 *
 * Ховер ничего больше не меняет: ни заливки, ни границы. Строка не
 * «выделяется» — на ней просто проступает то, что к ней применимо.
 *
 * Место под кнопку зарезервировано всегда (гасим прозрачностью, а не
 * `display`), иначе текст вопроса дёргался бы под курсором.
 */
const HOVER_ACTION = '[data-hover-action]';

const QuestionPanel = styled(SplitPanel)({
  [HOVER_ACTION]: {
    opacity: 0,
    transition: `opacity ${MotionDurationBase} ${MotionEasingStandard}`,
  },
  // Фокус наравне с ховером: с клавиатуры кнопка иначе недостижима —
  // видимого фокуса на невидимом элементе не бывает.
  [`&:hover ${HOVER_ACTION}, &:focus-within ${HOVER_ACTION}`]: {
    opacity: 1,
  },
  // Там, где ховера не существует, скрывать нечем и незачем.
  '@media (hover: none)': {
    [HOVER_ACTION]: {opacity: 1},
  },
});

const Ordinal = styled(Box)(({theme}) => ({
  ...text('label.m'),
  flex: 'none',
  width: 28,
  height: 28,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.primary.container,
  color: theme.palette.primary.onContainer,
}));

const QuestionText = styled(Box)({
  ...text('heading.m'),
  color: surfaceVar('onSurface'),
  maxWidth: MEASURE_WIDE,
});

const Basis = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceMuted'),
  maxWidth: MEASURE,
});

/** Образец ответа: зелёное правило слева и разбор под текстом. */
const Sample = styled(Box)(({theme}) => ({
  paddingLeft: spacingPx('m'),
  borderLeft: `3px solid ${theme.palette.good.main}`,
}));

const SampleVerdict = styled(Box)(({theme}) => ({
  ...text('label.m'),
  color: theme.palette.good.main,
}));

/** Сам ответ набран курсивом — это чужая речь, а не наш текст. */
const SampleText = styled(Box)({
  ...text('body.l'),
  color: surfaceVar('onSurface'),
  maxWidth: MEASURE,
  fontStyle: 'italic',
});

const SampleWhy = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceLight'),
  maxWidth: MEASURE,
});

/** Только рабочие образцы: отговорки в раскрытии больше не показываем. */
function goodAnswers(answers: Question['answers']): AnswerExample[] {
  return (answers ?? []).filter(answer => answer.quality === 'GOOD');
}

/**
 * Образец рабочего ответа.
 *
 * Плохой вариант убран: он учил распознавать отговорку, но не тому, каким
 * ответ должен быть. Раз в раскрытии остался один тон, разбирать его
 * пропом `$good` больше нечего — зелёный здесь единственно возможный.
 */
function Sampled({answer}: {answer: AnswerExample}) {
  return (
    <Sample>
      <Stack gap='xs'>
        <Row gap='xs' alignY='center'>
          <Icon name='check_circle' size='s' color='good' />
          <SampleVerdict>Так выглядит рабочий ответ</SampleVerdict>
        </Row>
        <SampleText>«{answer.text}»</SampleText>
        <SampleWhy>{answer.why}</SampleWhy>
      </Stack>
    </Sample>
  );
}

export function QuestionsSection({questions}: QuestionsSectionProps) {
  // В буфер уезжает текст для планёрки или письма — служебные символы
  // типографа там только мешают поиску по строке.
  const allQuestions = questions
    .map((q, i) => stripTypography(`${i + 1}. ${q.question}`))
    .join('\n');

  return (
    <Stack gap='m'>
      <SplitCard direction='column' reflowAt='never'>
        {questions.map((item, index) => (
          <QuestionPanel key={item.question} padding='l'>
            <Row gap='m' alignY='start' reflowAt='md'>
              <Ordinal>{index + 1}</Ordinal>
              <Stack gap='s'>
                <QuestionText>{item.question}</QuestionText>
                <Basis>Основание: {item.basis}</Basis>
                <Disclosure label='Пример хорошего ответа' labelOpen='Свернуть'>
                  <Stack gap='m'>
                    {goodAnswers(item.answers).map(answer => (
                      <Sampled key={answer.text} answer={answer} />
                    ))}
                  </Stack>
                </Disclosure>
              </Stack>
              <Box data-hover-action>
                <CopyButton
                  value={stripTypography(item.question)}
                  label='Копировать'
                  size='m'
                  variant='ghost'
                  color='neutral'
                />
              </Box>
            </Row>
          </QuestionPanel>
        ))}
      </SplitCard>

      <Row gap='s' justify='end'>
        <CopyButton
          value={allQuestions}
          label='Скопировать все вопросы'
          size='m'
          variant='outline'
          color='primary'
        />
      </Row>
    </Stack>
  );
}
